import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Phone, Star, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { RESTAURANT, IMAGES as DEFAULT_IMAGES, TESTIMONIALS, FAQS } from "../data/restaurant";
import { useMenu } from "../context/MenuContext";
import TrustBadges from "../components/TrustBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { categories } = useMenu();
  const pizzasCat = categories.find((c) => c.slug === "pizzas");
  const featured = pizzasCat?.items?.slice(0, 3) || [];

  // Admin can override any home-page image; fall back to bundled defaults.
  const [overrides, setOverrides] = useState({});
  useEffect(() => {
    axios
      .get(`${API}/site/home-images`)
      .then((r) => setOverrides(r.data || {}))
      .catch(() => {});
  }, []);
  const IMAGES = useMemo(() => ({ ...DEFAULT_IMAGES, ...overrides }), [overrides]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-[#0E0E1A] text-white"
      >
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 -right-20 w-[600px] h-[600px] rounded-full bg-[#E5B07D]/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 w-[500px] h-[500px] rounded-full bg-[#C9853E]/15 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay bg-[radial-gradient(circle_at_30%_20%,rgba(229,176,125,0.4),transparent_50%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-6">
              <span className="w-8 h-px bg-[#E5B07D]" />
              Lethbridge · Alberta
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-white">
              Where{" "}
              <span className="font-accent italic font-medium bg-gradient-to-r from-[#E5B07D] via-[#C9853E] to-[#E5B07D] bg-clip-text text-transparent">
                Indian flavors
              </span>{" "}
              meet pizza{" "}
              <span className="relative inline-block">
                perfection
                <svg
                  viewBox="0 0 200 12"
                  className="absolute -bottom-2 left-0 w-full"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 50 2, 100 6 T 198 5"
                    stroke="#E5B07D"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              .
            </h1>
            <p className="mt-7 text-lg text-white/75 max-w-xl leading-relaxed">
              Tandoor-fired pizzas, slow-simmered curries, and naan pulled fresh from the oven —
              a one-of-a-kind fusion experience crafted for Lethbridge.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/menu"
                data-testid="hero-order-now-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9853E] to-[#E5B07D] hover:from-[#A6692D] hover:to-[#C9853E] text-white rounded-full px-7 py-4 font-bold transition-all shadow-[0_10px_30px_-8px_rgba(201,133,62,0.7)] hover:-translate-y-0.5"
              >
                Order Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/feasts"
                data-testid="hero-feasts-btn"
                className="glass-pill inline-flex items-center gap-2 text-white hover:text-[#E5B07D] rounded-full px-7 py-4 font-bold transition-all border border-white/20"
              >
                Build a Feast
              </Link>
              <a
                href={`tel:${RESTAURANT.phoneRaw}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 text-white/80 hover:text-[#E5B07D] rounded-full px-5 py-4 font-bold transition-all"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>

            <div className="mt-10">
              <TrustBadges variant="dark" />
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] md:aspect-[5/6] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] rotate-[1.5deg] hover:rotate-0 transition-transform duration-700 ring-1 ring-[#C9853E]/30">
              <img
                src={IMAGES.heroPizza}
                alt="Wood-fired Indian fusion pizza"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between bg-[#0E0E1A]/90 backdrop-blur rounded-2xl px-5 py-3.5 shadow-lg border border-[#C9853E]/30">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold">Signature</div>
                  <div className="font-display font-bold text-white">Butter Chicken Pizza</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-white/60">Indian Pizza</div>
                  <div className="font-display font-bold text-white">Choose Size</div>
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute -left-10 -bottom-8 w-44 h-44 rounded-3xl overflow-hidden shadow-2xl border-4 border-[#0E0E1A] -rotate-6">
              <img src={IMAGES.naan} alt="Garlic naan" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:flex absolute -top-6 -right-4 bg-gradient-to-r from-[#C9853E] to-[#E5B07D] text-[#0E0E1A] px-5 py-3 rounded-2xl items-center gap-2 shadow-2xl rotate-3">
              <Star className="w-4 h-4 fill-[#0E0E1A] text-[#0E0E1A]" />
              <span className="text-sm font-bold">Locals' favorite</span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE — warm saffron strip (logo gold) */}
      <section className="border-y border-[#A6692D]/40 bg-gradient-to-r from-[#A6692D] via-[#C9853E] to-[#A6692D] overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-5">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12 text-white">
              {[
                "Tandoor-fired pizzas",
                "★",
                "Authentic curries",
                "★",
                "Fresh naan, every order",
                "★",
                "Ready in ~12 minutes",
                "★",
                "Apple Pay friendly",
                "★",
              ].map((t, i) => (
                <span key={i} className="font-accent italic text-2xl md:text-3xl text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]">
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* BENTO HIGHLIGHTS — warm cream (logo cream) */}
      <section data-testid="highlights-section" className="bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-3">
              The Menu
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A] leading-tight">
              A flavor-forward<br /> fusion you've never tasted.
            </h2>
          </div>
          <Link
            to="/menu"
            data-testid="see-full-menu-link"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#0E0E1A] hover:text-[#C9853E] transition-colors group"
          >
            See the full menu
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          <div className="md:col-span-8 md:row-span-2 relative rounded-3xl overflow-hidden group min-h-[420px] md:min-h-[560px]">
            <img src={IMAGES.margherita} alt="Indian pizza" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E1A] via-[#0E0E1A]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-2">01 / Pizzas</div>
              <h3 className="font-display text-3xl md:text-5xl font-extrabold text-white leading-tight max-w-md">
                Indian-style<br /> fusion pizzas
              </h3>
              <p className="text-white/80 mt-3 max-w-md">
                Hand-stretched dough topped with butter chicken, tandoori paneer, lamb keema and more.
              </p>
            </div>
          </div>

          <div className="md:col-span-4 relative rounded-3xl overflow-hidden group min-h-[260px]">
            <img src={IMAGES.curry} alt="Indian curry" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E1A]/90 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-1">02 / Curries</div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                Slow-simmered<br /> traditional curries
              </h3>
            </div>
          </div>

          <div className="md:col-span-4 relative rounded-3xl overflow-hidden group min-h-[260px]">
            <img src={IMAGES.naan} alt="Naan bread" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E1A]/90 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-1">03 / Breads</div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                Fresh tandoor<br /> naan & sides
              </h3>
            </div>
          </div>
        </div>

        {/* Featured pizzas grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {featured.map((item, i) => (
            <article
              key={item.id || i}
              data-testid={`featured-item-${i}`}
              className="group bg-white rounded-2xl border border-[#E7E5E4] p-6 hover:-translate-y-1 hover:shadow-xl hover:border-[#C9853E]/50 transition-all"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="font-display text-xl font-bold text-[#0E0E1A]">{item.name}</h4>
                <span className="font-display font-bold text-[#C9853E] text-sm md:text-base whitespace-nowrap">
                  {item.price != null ? `$${item.price.toFixed(2)}` : item.note}
                </span>
              </div>
              {item.tags?.includes("signature") && (
                <span className="inline-block mt-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#C9853E]">
                  Signature
                </span>
              )}
              <p className="text-sm text-[#57534E] mt-3 leading-relaxed">{item.desc}</p>
            </article>
          ))}
        </div>
        </div>
      </section>

      {/* STORY — deep charcoal with warm gold glow (logo palette) */}
      <section data-testid="story-section" className="relative bg-[#0E0E1A] text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#C9853E]/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#A6692D]/15 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[#C9853E]/30">
              <img src={IMAGES.tandoor} alt="Tandoor cooking" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-10 -right-8 w-48 h-48 rounded-3xl overflow-hidden border-4 border-[#0E0E1A] shadow-2xl rotate-6">
              <img src={IMAGES.spices} alt="Indian spices" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <div className="text-xs tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-4">
              Our Story
            </div>
            <h2 className="font-accent italic text-4xl md:text-5xl text-white leading-tight">
              Two cuisines.<br />
              <span className="text-[#E5B07D]">One unforgettable bite.</span>
            </h2>
            <p className="mt-6 text-lg text-white/80 leading-relaxed">
              Rivaaz is a love letter to the streets of India and the corner pizzeria — two
              great traditions, fired in the same tandoor. We hand-stretch our dough, blend our
              own masalas, and braise every curry the way our grandmothers did. Then we build
              pizzas no one else dares to.
            </p>
            <Link
              to="/about"
              data-testid="story-cta"
              className="inline-flex items-center gap-2 mt-8 text-sm font-bold text-[#E5B07D] hover:text-white transition-colors group"
            >
              Read the full story
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="bg-[#0E0E1A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={IMAGES.spices} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-xs tracking-[0.25em] uppercase text-[#E5B07D] font-bold mb-4">
              Loved in Lethbridge
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
              People keep coming back<br />
              <span className="font-accent italic font-medium text-[#E5B07D]">for one more bite.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
              >
                <Quote className="w-6 h-6 text-[#E5B07D] mb-4" />
                <p className="text-[15px] leading-relaxed text-white/90">"{t.quote}"</p>
                <div className="mt-5 flex items-center justify-between">
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} className="w-3.5 h-3.5 fill-[#E5B07D] text-[#E5B07D]" />
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — warm cream */}
      <section data-testid="faq-section" className="bg-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-3">
            FAQ
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A]">
            Good to know
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-[#E7E5E4]">
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#0E0E1A] hover:text-[#C9853E] py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#57534E] leading-relaxed text-base pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="bg-[#C9853E] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.05]">
              Hungry yet?<br />
              <span className="font-accent italic font-medium">Let's fix that.</span>
            </h2>
            <p className="mt-5 text-white/85 text-lg max-w-md">
              Most orders ready in about 12 minutes. Call us, swing by, and grab dinner the family will fight over.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link
              to="/menu"
              data-testid="final-order-btn"
              className="inline-flex items-center gap-2 bg-white text-[#C9853E] hover:bg-[#0E0E1A] hover:text-white rounded-full px-7 py-4 font-bold transition-all"
            >
              Start your order <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              data-testid="final-call-btn"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#C9853E] rounded-full px-7 py-4 font-bold transition-all"
            >
              <Phone className="w-4 h-4" /> {RESTAURANT.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
